/**
 * i18n bootstrap - initialises i18next with the English catalogue.
 *
 * Per the v2 plan decision: every user-facing string goes through the
 * `t()` wrapper from day 0, even though the catalogue ships English-
 * only until v2.1 lands an Arabic translation. The discipline is the
 * point - see [[feedback_do_the_hard_things_now]].
 *
 * Catalogue lives in `./en.ts`. New modules ADD to it (or eventually
 * to a module-scoped namespace once the en catalogue grows past one
 * file). Adding a new visible string in a component means:
 *
 *   1. Add the key + English text to `./en.ts`.
 *   2. Use `t("your.key")` in the component (NOT a bare string).
 *
 * The ESLint rule landing in P0.8 will warn on bare string literals
 * inside JSX children. For now the discipline is documented.
 */
import i18n from "i18next";
import { localeFor } from "shared/time/formatDate";
import { initReactI18next } from "react-i18next";

import { mirrorArrows } from "shared/i18n/arrows";

import { ar } from "shared/i18n/ar";
import { catalogueState, type CatalogueState } from "shared/i18n/catalogueState";
import { en } from "shared/i18n/en";
import { pseudoArabic } from "shared/i18n/pseudoArabic";
import { sama } from "shared/i18n/regime/sama";

export const SUPPORTED_LANGS = ["en", "ar"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

const STORAGE_KEY = "marsad.lang";
/** Round 18 K23 -- set only when the reader picks a language with the
 *  control, so the sign-in can tell a choice from a default. */
export const LANGUAGE_CHOSEN_KEY = "marsad.lang.chosen";

/**
 * The dev-only pseudo-locale's tag.
 *
 * A BCP-47 private-use subtag, so `Intl.PluralRules` resolves it to
 * Arabic and i18next asks for all six categories -- which is the whole
 * reason it is not registered as `ar`. It is deliberately not a member
 * of `Lang`: `setLanguage` cannot reach it, the toggle cannot offer it,
 * and a stored preference cannot select it.
 */
export const PSEUDO_LANG = "ar-x-pseudo";

/**
 * G4 -- computed once at load, from the catalogue itself.
 *
 * `scaffold` means no string has been translated, so Arabic is not on
 * offer: LanguageToggle renders nothing and a stored `ar` preference
 * is ignored below. The second half matters as much as the first --
 * hiding the control without resetting the language would strand
 * anyone who had already switched, in RTL, with no way back.
 */
export const AR_CATALOGUE_STATE: CatalogueState = catalogueState(ar);
export const AR_IS_OFFERED = AR_CATALOGUE_STATE !== "scaffold";

/** P127: apply the document direction + lang for the active language.
 * Arabic is RTL; everything else LTR. Layout is already RTL-safe (logical
 * utilities), so this is the only DOM hook the switch needs. */
/** One rule for "is this reader right-to-left", used by the document
 *  direction and by the arrow mirror, so the two cannot disagree.
 *  `startsWith`, not equality: the dev pseudo-locale is `ar-x-pseudo`. */
export function isRtlLang(lng: string | undefined): boolean {
  return !!lng && lng.startsWith("ar");
}

function applyDocumentDir(lng: string): void {
  if (typeof document === "undefined") return;
  // `startsWith`, not equality: the dev pseudo-locale is `ar-x-pseudo`
  // and exists precisely to exercise right-to-left, so an exact match
  // would have left it drawing left-to-right and finding nothing.
  document.documentElement.dir = isRtlLang(lng) ? "rtl" : "ltr";
  document.documentElement.lang = documentLangTag(lng);
}

/**
 * D9 (round 8) -- the region a native date control needs.
 *
 * `<input type="date">` and `type="time"` take their display format
 * from the nearest `lang`, and a BARE "en" is mapped by Chromium to US
 * conventions -- so every date control in the product rendered
 * mm/dd/yyyy for seventh round running, under copy written in British
 * English for a Saudi deployment. There is no attribute that sets the
 * format directly; the language tag is the only lever, and a bare
 * language tag is genuinely ambiguous about it.
 *
 * So the tag carries the region we actually mean. The catalogue key
 * stays "en" / "ar" -- this is about what the BROWSER is told, not
 * about which catalogue is loaded, and conflating the two is what
 * would make adding a locale hard later.
 */
export function documentLangTag(lng: string): string {
  if (lng === "en") return "en-GB";
  if (lng === "ar") return "ar-SA";
  return lng;
}

function readStoredLang(): Lang | typeof PSEUDO_LANG {
  // Defensive: some test/SSR environments expose a partial `localStorage`
  // whose methods aren't functions (the jsdom Storage-shim gotcha), or
  // throw SecurityError on access. Feature-detect + swallow.
  try {
    const stored = globalThis.localStorage?.getItem?.(STORAGE_KEY);
    // D1 -- the pseudo-locale did not survive a page load.
    //
    // The languageChanged handler WROTE "ar-x-pseudo" to storage and
    // this function never read it back, so any full load fell to
    // English. The round 5 RTL pass measured twelve of seventy one
    // URLs rendering left-to-right, which read as a flaky harness and
    // was a real defect: a developer who selects the pseudo-locale and
    // reloads should still be in it.
    //
    // Dev-only, like the locale itself. A production build has no
    // pseudo resources, so restoring it there would select a language
    // with no catalogue behind it.
    if (stored === PSEUDO_LANG && import.meta.env.DEV) return PSEUDO_LANG;
    /*
     * Round 10 E1 -- the direction did not survive a route change.
     *
     * Two doors, one guarded: `setLanguage` refuses `ar` while the
     * catalogue is a scaffold, but `i18n.changeLanguage("ar")` does
     * not, and anybody measuring the Arabic build uses the second. So
     * forcing it worked -- rtl, mirrored layout -- and the first full
     * load put the product back in English, which reads as the
     * direction being lost on navigation rather than as a locale that
     * is not offered.
     *
     * A forced `ar` is a DEV affordance, exactly like the pseudo-
     * locale beside it, and it is restored on the same terms: what you
     * measure on home is then what you measure on /admin. In
     * production an un-offered locale still cannot be restored,
     * because nothing there can select it in the first place.
     */
    if (stored === "ar" && (AR_IS_OFFERED || import.meta.env.DEV)) return "ar";
    return "en";
  } catch {
    return "en";
  }
}

/**
 * Wrap an interpolated value in a bidi isolate -- but only one that
 * needs it.
 *
 * Round 6 put U+2068/U+2069 around every interpolated value in an RTL
 * language, which fixed identifiers torn out of the sentences carrying
 * them. Round 7's E1 is that fix overreaching: an isolate makes its
 * contents a separate directional run, and a run at the START of an RTL
 * line is placed rightmost, so a leading count landed on the far side
 * of the words it belonged to and twenty five supervisory-check
 * summaries read as their fraction reversed.
 *
 * The distinction is what the value CONTAINS, not where it sits. A bare
 * number cannot reorder against neighbouring text -- the bidi algorithm
 * already folds European digits into the adjacent run, which is exactly
 * why "10 of 14" was right before the isolate was added and wrong
 * after. A value carrying anything else -- "3.1.1-CR7", "T24 / mada", a
 * date, a path -- has internal structure the algorithm will rearrange,
 * and that is what the isolate is for.
 *
 * Deciding on position instead ("isolate unless it is first") was the
 * obvious alternative and is wrong: the same value leads one sentence
 * and sits mid-way through another, so it would isolate inconsistently,
 * and a translator moving a placeholder would silently change how it
 * renders.
 */
const BARE_NUMBER = /^[+-]?[\d,.\u0660-\u0669\u06F0-\u06F9]+%?$/;

/**
 * Round 14 E4 -- a count reads the way a person writes it.
 *
 * "100 shown of 20000 in the register" sat beside "2,404 services":
 * i18next interpolates a number exactly as given, so every count that
 * reached a sentence through `t()` lost its grouping while the few
 * formatted by hand kept theirs. Done here, at the one place every
 * interpolated value passes, so a new string cannot miss it.
 *
 * Integers of four digits and up only. A year or a code is a string by
 * the time it is interpolated, and a small count reads the same either
 * way. The `count` option i18next uses to choose a plural form is a
 * separate value and is untouched.
 */
export function groupedCount(value: unknown, lng?: string): unknown {
  if (typeof value !== "number" || !Number.isInteger(value) || Math.abs(value) < 1000) {
    return value;
  }
  return new Intl.NumberFormat(localeFor(lng)).format(value);
}

export function isolateValue(value: unknown, lng?: string): string {
  const text = String(value);
  if (!lng || !lng.startsWith("ar")) return text;
  if (BARE_NUMBER.test(text)) return text;
  return `\u2068${text}\u2069`;
}

const initialLang = readStoredLang();

/**
 * C3 -- mirror catalogue arrows for a right-to-left reader.
 *
 * A post-processor rather than 39 call-site edits: the arrows live
 * inside translated prose, several of them inside interpolated
 * sentences ("cut RTO {{current}}h -> {{target}}h"), where a component
 * cannot reach. One place decides which way an arrow points, so the
 * cycle page's back link and the analyser's -- one click apart, one
 * rendered by <Arrow> and one from the catalogue -- can no longer
 * disagree.
 */
const arrowMirror = {
  type: "postProcessor" as const,
  name: "mirrorArrows",
  process(
    value: string,
    _key: string,
    _opts: unknown,
    translator: { language?: string },
  ) {
    return mirrorArrows(value, isRtlLang(translator?.language));
  },
};

void i18n
  .use(initReactI18next)
  .use(arrowMirror)
  .init({
    postProcess: ["mirrorArrows"],
    resources: {
      en: { translation: en },
      // P127: scaffold catalogue (English marked "pending translation").
      // A translator replaces shared/i18n/ar.ts with a real object.
      ar: { translation: ar },
      // A pseudo-locale for LAYOUT, dev builds only. Arabic script of
      // realistic length, with all six plural forms -- see
      // shared/i18n/pseudoArabic.ts for why this is not a translation and
      // must never be mistaken for one.
      ...(import.meta.env.DEV
        ? { [PSEUDO_LANG]: { translation: pseudoArabic(en) as typeof en } }
        : {}),
    },
    lng: initialLang,
    fallbackLng: "en",
    interpolation: {
      escapeValue: false, // React already escapes
      /*
       * P415: the regime terms, defaulted to what the catalogue's
       * literals already said.
       *
       * The strings carry `{{serviceTerm}}` and friends in about two
       * hundred places, and an interpolation with no variable renders
       * the BRACES on screen. `RegimeTerms` supplies the live regime's
       * terms once the bank's profile has loaded, but that leaves two
       * gaps it cannot cover: the moments before it mounts, and every
       * test that renders a component without it. Both would show
       * "{{serviceTerm}}".
       *
       * So the floor is set here, at init, to SAMA's English terms --
       * which are the exact words that were in the catalogue before
       * this phase. A deployment that records no regime therefore
       * renders precisely what it rendered yesterday; this is the
       * status quo written down, not a regulator asserted. The moment
       * a regime IS recorded, RegimeTerms overwrites these.
       */
      defaultVariables: Object.fromEntries(
        Object.entries(sama.en ?? {}).filter(([, v]) => typeof v === "string"),
      ) as Record<string, string>,
      // D2 -- isolate every interpolated value in a right-to-left
      // language.
      //
      // The dominant right-to-left defect class was not layout: it was
      // figures and identifiers torn out of the sentences that carried
      // them. "6h declared, awaiting the CRO" put the 6h at the far end;
      // an ISO date rendered 21-09-2026; "+24h" became "24+" with a
      // detached h; a control code split in half across a row.
      //
      // Every one of those is an interpolated value inside translated
      // prose, so the fix belongs at the interpolation rather than at
      // several hundred call sites -- which is also the only version of
      // it that a new string cannot escape.
      //
      // U+2068 FIRST STRONG ISOLATE and U+2069 POP DIRECTIONAL ISOLATE
      // are the characters Unicode provides for precisely this. They are
      // characters, not markup, so they work through plain `t()` where a
      // <bdi> element cannot reach; FIRST STRONG means a value that is
      // itself Arabic still reads right-to-left, so this isolates
      // without imposing a direction.
      //
      // Left-to-right languages are untouched: there is nothing to
      // reorder, and invisible characters in an English string would
      // break every test that compares one.
      // `format` is only called for values that NAME a format
      // ({{x, uppercase}}) unless this is set, and almost none of ours
      // do. Without it the hook below is dead code that looks live --
      // measured with the RTL drive, which kept reporting the same
      // defects after the "fix" landed.
      alwaysFormat: true,
      format: (value, format, lng) => {
        // P415: a regime term is one word in the vocabulary and has to
        // read correctly in two grammatical positions -- "the
        // {{serviceTerm}} failed" mid-sentence and
        // "{{serviceTerm, capitalize}} admitted" at the start of one.
        //
        // A formatter rather than three keys per term, because a
        // second key is a second thing to keep in step and a regime
        // could ship them disagreeing. Sentence case only: TITLE case
        // is a styling choice SAMA makes about its own terms and OSFI
        // does not -- E-21 writes "critical operation" in running
        // prose -- so those few places stay literal and are decided
        // one at a time.
        if (format === "capitalize") {
          const text = String(value);
          const cased = text.charAt(0).toUpperCase() + text.slice(1);
          return isolateValue(cased, lng);
        }
        return isolateValue(groupedCount(value, lng), lng);
      },
    },
    // Strict catch for missing keys during dev - surface them rather
    // than silently rendering the key. v2.1 RTL will need every string
    // present in the Arabic catalogue too; missing-key noise today
    // catches the gaps before they hit translation.
    returnNull: false,
    returnEmptyString: false,
    saveMissing: true,
    missingKeyHandler: (_lngs, _ns, key) => {
      // Intentional console.error: vitest's onConsoleLog (see
      // vitest.config.ts) treats stderr as a test failure, so a missing
      // key gets caught loudly in both dev and CI.
      console.error(`[i18n] missing key: ${key}`);
    },
  });

// Keep the document direction + stored preference in lockstep with the
// active language. Runs on every switch + once for the initial language.
i18n.on("languageChanged", (lng) => {
  applyDocumentDir(lng);
  try {
    globalThis.localStorage?.setItem?.(STORAGE_KEY, lng);
  } catch {
    // Storage unavailable / partial shim -- preference just won't persist.
  }
});
applyDocumentDir(initialLang);

/** Switch the UI language (and, as a side effect, the document dir). */
export function setLanguage(lng: Lang): void {
  // A caller cannot select a language the deployment does not offer.
  // The toggle is already hidden; this is the belt to that braces, so
  // a stale bookmark or a console call cannot reach the scaffold.
  if (lng === "ar" && !AR_IS_OFFERED) return;
  try {
    globalThis.localStorage?.setItem?.(LANGUAGE_CHOSEN_KEY, lng);
  } catch {
    // Storage unavailable: the sign-in then follows the browser.
  }
  void i18n.changeLanguage(lng);
}

/**
 * G3 -- a way to exercise right-to-left without offering it.
 *
 * G4 withdrew the language control because the Arabic catalogue is
 * still a scaffold, which is correct for a reader and awkward for a
 * verifier: G3's whole acceptance is "the RTL screenshots show no
 * clipped chrome", and the layout can no longer be reached through
 * the product.
 *
 * So the instance is exposed on `window` in DEV BUILDS ONLY. A drive
 * script can flip the direction; a production bundle has no such
 * property, so this is not a route back to the scaffold. Verification
 * scaffolding, not a feature -- which is why it is not a query
 * parameter or a hidden key chord that a user could stumble into.
 */
if (import.meta.env.DEV && typeof window !== "undefined") {
  (window as unknown as { __marsadI18n?: typeof i18n }).__marsadI18n = i18n;
}

/**
 * Is the pseudo-locale showing?
 *
 * Asked by the banner that says so. Kept here rather than compared at
 * the call site so there is one place that knows the tag.
 */
export function isPseudoLocale(lng: string | undefined): boolean {
  return lng === PSEUDO_LANG;
}

export { i18n };
